import type { FC, ReactNode } from 'react'
import type { CompanyName } from '@/components/art/wordmark'
import type { PhotoId } from '@/components/generals/photo'

import { cn } from '@/lib/utils'
import { useStackGiveWay } from '@/lib/stack-give-way'
import { Wordmark } from '@/components/art/wordmark'
import { Wash } from '@/components/generals/backdrops'
import { ButtonLink } from '@/components/generals/button-link'
import { HiringManagerPane } from '@/components/generals/hiring-manager-pane'
import { routes } from '@/constants/site'

const SECTION = 'section-x relative isolate mx-auto max-w-[calc(1080px+2*clamp(16px,3vw,32px))]'

/* Want to see more? ------------------------------------------------------------------ */

export const WantToSeeMore: FC = (): ReactNode => {
    return (
        <section id='see-more' aria-labelledby='see-more-title' className={cn(SECTION, 'py-[90px] max-sm:py-14')}>
            <div className='more-field reveal flex flex-col items-center overflow-hidden rounded-[6px] px-[clamp(24px,4vw,64px)] py-[clamp(56px,7vw,96px)] text-center'>
                <h2 id='see-more-title' className='type-section'>
                    Want to see more?
                </h2>
                <p className='mx-auto mt-4 max-w-[46ch] text-ink/80'>
                    The full list is private, but if you're hiring, let us know and we'll share it with you
                </p>
                <ButtonLink href={routes.getStarted} size='lg' className='mt-7 max-sm:h-10'>
                    Get started
                </ButtonLink>
            </div>
        </section>
    )
}

/* From the teams who hired ------------------------------------------------------------ */

interface Testimonial {
    company: CompanyName
    quote: string
    name: string
    role: string
    photo: PhotoId
}

const TESTIMONIALS: Testimonial[] = [
    {
        company: 'Tessel',
        quote: 'Normally you spend weeks calibrating a recruiter. Here the taste was right from the first shortlist.',
        name: 'Clara Duval',
        role: 'Design Director',
        photo: 'elena'
    },
    {
        company: 'Northfold',
        quote: 'We got a private signal on designers who were quietly open to moving, something we could never find ourselves.',
        name: 'Elliot Brandt',
        role: 'CEO',
        photo: 'luca'
    },
    {
        company: 'Halyard',
        quote: 'Everyone who came through was genuinely good at the craft, not just good at interviewing.',
        name: 'Marta Lindqvist',
        role: 'Head of Product Design',
        photo: 'nora'
    },
    {
        company: 'Kiln',
        quote: "Our odds went from a couple of percent to closer to a coin flip. That's worth my time.",
        name: 'Jonas Weir',
        role: 'Founder',
        photo: 'mateo'
    }
]

const STICKY_TOP = 100

/** Stacked frayed cards: the quote on one side, the hiring manager's pane on the other, alternating. */
export const Testimonials: FC = (): ReactNode => {
    const cardRefs = useStackGiveWay(STICKY_TOP)

    return (
        <section id='testimonials' aria-labelledby='testimonials-title' className={cn(SECTION, 'py-[90px] max-[991px]:pt-[63px] max-sm:py-14')}>
            <Wash strength='strong' fade={300} />

            <h2 id='testimonials-title' className='sr-only'>
                From the teams who hired
            </h2>

            <ul className='flex flex-col gap-12 max-md:gap-6'>
                {TESTIMONIALS.map((item, index) => (
                    <li
                        key={item.name}
                        ref={element => {
                            cardRefs.current[index] = element
                        }}
                        className={cn(
                            'reveal relative isolate flex min-h-[clamp(420px,33vw,480px)] origin-top p-2 max-md:min-h-0 max-md:flex-col md:sticky md:top-[100px] md:will-change-transform',
                            index % 2 === 1 && 'md:flex-row-reverse'
                        )}
                    >
                        <span aria-hidden='true' className='fray pointer-events-none absolute inset-0 -z-10 rounded-[2px] bg-card' />
                        <span aria-hidden='true' className='pointer-events-none absolute inset-0 z-[5] rounded-[2px] bg-background opacity-[var(--dim,0)]' />

                        <figure className='flex flex-1 basis-1/2 flex-col p-8 max-md:px-[18px] max-md:pb-[22px] max-md:pt-5'>
                            <Wordmark name={item.company} className='self-start text-[18px] text-ink/90 max-md:text-[13px]' />

                            <div className='mt-auto flex max-w-[428px] flex-col gap-[18px] pt-10 max-md:gap-3 max-md:pt-4'>
                                <blockquote className='type-section'>
                                    <p>“{item.quote}”</p>
                                </blockquote>
                                <figcaption className='flex flex-wrap items-baseline gap-1 text-[14px] leading-5 text-ink/[0.58] max-md:text-[13px] max-md:leading-[18px]'>
                                    <span className='font-medium'>{item.name},</span>
                                    <span>{item.role}</span>
                                </figcaption>
                            </div>
                        </figure>

                        <div className='flex-1 basis-1/2 max-md:flex-none'>
                            <HiringManagerPane photo={item.photo} name={item.name} />
                        </div>
                    </li>
                ))}
            </ul>
        </section>
    )
}
